import { ChangeEvent, useEffect, useState } from 'react'

import {
  GoogleMapsProvider,
  useGoogleMaps
} from 'src/providers/GoogleMapsProvider'

import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { ParagraphSmaller } from 'src/components/atoms/Typography'
import { Map } from 'src/components/molecules/Map'
import { MapAutocomplete } from 'src/components/molecules/MapAutocomplete'
import { VStack } from 'src/components/templates/VStack'

import { GeoData } from 'src/types/map'
import { geoCodePositionFromMarker, isValidGeo } from 'src/utils/map'

export interface MapLocationSearchProps {
  autocompleteProps: {
    description?: string | JSX.Element
    id: string
    label: string
    placeholder?: string
  }
  helpText?: string | JSX.Element
  geoData: GeoData
  onPin: (geoData: GeoData) => void
  showMap?: boolean
}

export const MapLocationSearch = ({
  autocompleteProps,
  helpText,
  geoData,
  onPin,
  showMap = true
}: MapLocationSearchProps) => {
  const [address, setAddress] = useState<string>('')
  const [geo, setGeo] = useState<GeoData>(geoData)

  const { isLoaded } = useGoogleMaps()

  useEffect(() => {
    async function fetchGeoCode() {
      if (isValidGeo(geo)) {
        const placeCode = await geoCodePositionFromMarker(geo)
        if (placeCode.address) setAddress(placeCode.address)
      }
    }

    fetchGeoCode()
  }, [geo])

  useEffect(() => {
    setGeo(geoData)
  }, [geoData])

  const handlePin = async (geoLocation: google.maps.LatLngLiteral) => {
    const placeCode = await geoCodePositionFromMarker(geoLocation)
    onPin(placeCode.geoLocation)
    if (placeCode.address) setAddress(placeCode.address)
  }

  const handleSelectPlace = async (data: GeoData) => {
    if (isValidGeo(data)) {
      const placeCode = await geoCodePositionFromMarker(data)
      // call onPin to set the Geo in formik
      onPin(placeCode.geoLocation)
      if (placeCode.address) setAddress(placeCode.address)
    }
  }

  const handleAutocompleteChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value)
  }

  return (
    <GoogleMapsProvider>
      <LoadingSpinner isLoading={!isLoaded}>
        <div className='mb-5 md:mb-10'>
          <MapAutocomplete
            {...autocompleteProps}
            onChange={handleAutocompleteChange}
            onSelectPlace={handleSelectPlace}
            value={address}
          />
          {showMap ? (
            <VStack className='mb-[10px]'>
              <Map
                geoData={geo}
                onPin={handlePin}
                zoom={isValidGeo(geo) ? 12 : 5}
              />
            </VStack>
          ) : null}
          {helpText ? (
            <ParagraphSmaller className='form-help-text'>
              {helpText}
            </ParagraphSmaller>
          ) : null}
        </div>
      </LoadingSpinner>
    </GoogleMapsProvider>
  )
}
